import { api } from "@/lib/api";
import type { Page } from "@/features/users/api";

export interface TriageSuggestion {
  suggestion_id: string;
  category_code: string | null;
  category_name: string | null;
  priority: string;
  sentiment: string;
  confidence: number;
  reason: string;
  provider: string;
}

export interface DuplicateHit {
  id: string;
  ticket_number: string;
  title: string;
  status: string;
  similarity: number;
}

export interface KbSuggestion {
  id: string;
  title: string;
  summary: string | null;
  score: number;
}

export interface EngineerSuggestion {
  user_id: string;
  name: string;
  score: number;
  open_tickets: number;
  skill_level: number;
  reason: string;
}

export interface SlaRiskItem {
  id: string;
  ticket_number: string;
  title: string;
  customer_name: string | null;
  clock: string;
  due_at: string | null;
  risk: number;
  minutes_left: number;
}

export interface KbArticle {
  id: string;
  title: string;
  summary: string | null;
  category_code: string | null;
  is_published: boolean;
  author_id: string | null;
  updated_at: string;
}

export interface KbArticleDetail extends KbArticle {
  body: string;
  tags: string[] | null;
  created_at: string;
}

export interface KbArticleBody {
  title: string;
  summary?: string | null;
  body: string;
  category_code?: string | null;
  tags?: string[] | null;
  is_published?: boolean;
}

export interface EngineerSkill {
  id: string;
  user_id: string;
  skill: string;
  level: number;
}

// --- ticket AI ---
export const classifyTicket = (id: string) =>
  api.post<TriageSuggestion>(`/tickets/${id}/ai/classify`);
export const ticketDuplicates = (id: string) =>
  api.get<DuplicateHit[]>(`/tickets/${id}/ai/duplicates`);
export const ticketKbSuggestions = (id: string) =>
  api.get<KbSuggestion[]>(`/tickets/${id}/ai/kb-suggestions`);
export const suggestEngineer = (id: string) =>
  api.get<EngineerSuggestion[]>(`/tickets/${id}/ai/suggest-engineer`);
export const acceptSuggestion = (sid: string) =>
  api.post<{ message: string }>(`/ai/suggestions/${sid}/accept`);
export const dismissSuggestion = (sid: string) =>
  api.post<{ message: string }>(`/ai/suggestions/${sid}/dismiss`);
export const slaRisk = () => api.get<SlaRiskItem[]>("/ai/sla-risk");

// --- knowledge base ---
export const listKb = (params: { search?: string; page?: number; page_size?: number } = {}) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, String(v));
  const s = q.toString();
  return api.get<Page<KbArticle>>(`/kb-articles${s ? `?${s}` : ""}`);
};
export const getKb = (id: string) => api.get<KbArticleDetail>(`/kb-articles/${id}`);
export const createKb = (body: KbArticleBody) => api.post<KbArticleDetail>("/kb-articles", body);
export const updateKb = (id: string, body: Partial<KbArticleBody>) =>
  api.patch<KbArticleDetail>(`/kb-articles/${id}`, body);
export const deleteKb = (id: string) => api.delete<{ message: string }>(`/kb-articles/${id}`);

// --- engineer skills ---
export const listSkills = (userId: string) =>
  api.get<EngineerSkill[]>(`/users/${userId}/skills`);
export const setSkill = (userId: string, skill: string, level: number) =>
  api.put<EngineerSkill>(`/users/${userId}/skills`, { skill, level });
export const deleteSkill = (skillId: string) =>
  api.delete<{ message: string }>(`/skills/${skillId}`);
